// Sopwith 3D - follow camera.
//
// Side view looking down -Z at the XY plane the simulation lives in. The
// camera distance is chosen so exactly MAX_Y world units are visible
// vertically at z = 0; width follows the viewport aspect. Horizontally it
// tracks the player with a little look-ahead in the flight direction and
// critically damped smoothing, clamped to the world.

import * as THREE from 'three';
import { MAX_Y } from '../sim/constants';

const FOV = 38;
const LOOKAHEAD = 28;
const STIFFNESS = 5;

export class FollowCamera {
  readonly camera: THREE.PerspectiveCamera;
  readonly distance: number;
  private x = 0;
  private lookahead = 0;
  private initialised = false;
  worldWidth = 0;

  constructor(aspect: number) {
    this.distance = MAX_Y / 2 / Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    this.camera = new THREE.PerspectiveCamera(FOV, aspect, 1, 4000);
    this.camera.position.set(0, MAX_Y / 2, this.distance);
    this.camera.lookAt(0, MAX_Y / 2, 0);
  }

  /** Half the world width visible at z = 0. */
  get halfWidth(): number {
    return (MAX_Y / 2) * this.camera.aspect;
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** Jump straight to the target (level start, respawn). */
  snap(targetX: number): void {
    this.x = this.clamp(targetX);
    this.lookahead = 0;
    this.initialised = true;
    this.apply();
  }

  update(targetX: number, dirX: number, dt: number): void {
    if (!this.initialised) {
      this.snap(targetX);
      return;
    }
    const k = 1 - Math.exp(-STIFFNESS * dt);
    this.lookahead += (Math.sign(dirX) * LOOKAHEAD - this.lookahead) * k * 0.5;
    const goal = this.clamp(targetX + this.lookahead);
    this.x += (goal - this.x) * k;
    this.apply();
  }

  private clamp(x: number): number {
    const hw = this.halfWidth;
    if (this.worldWidth <= hw * 2) {
      return this.worldWidth / 2;
    }
    return THREE.MathUtils.clamp(x, hw, this.worldWidth - hw);
  }

  private apply(): void {
    this.camera.position.x = this.x;
  }
}
